import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/Card';
import { useAuth } from '../../context/AuthContext';
import {
  RefreshCw, Search, Download, ExternalLink, ShoppingBag, ArrowUp, ArrowDown, ImageOff, Info, CalendarDays,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// ---------------------------------------------------------------------------
// Ürün bazında satış — her ürün görseli ve adıyla, haftalık / aylık dönemler.
//
// Satış GA4 item tablosundan (AEM + legacy kuyruğu, bugün hariç); görsel/başlık
// hebiar feed'inde ürünün son göründüğü günden, yoksa Merchant Center'dan.
// Hücrede iki sayı birden: üstte ciro, altta adet (/high5/ciro-stok'ta tek
// metrik gösteren ilk sürüm reddedilmişti — aynı ders).
// ---------------------------------------------------------------------------

const GRAINS = [
  { key: 'week', label: 'Haftalık', ranges: [8, 12, 26, 52], def: 12, unit: 'hafta' },
  { key: 'month', label: 'Aylık', ranges: [3, 6, 12, 24], def: 6, unit: 'ay' },
];

const PAGE = 50;

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR');
const fmtMoney = (n) => `${Math.round(Number(n) || 0).toLocaleString('tr-TR')} ₺`;
const fmtMoneyShort = (n) => {
  const v = Math.round(Number(n) || 0);
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toLocaleString('tr-TR', { maximumFractionDigits: 2 })}M ₺`;
  if (Math.abs(v) >= 10000) return `${(v / 1000).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B ₺`;
  return `${v.toLocaleString('tr-TR')} ₺`;
};
const fmtDay = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return d; }
};
const periodLabel = (k, grain) => {
  try {
    const a = new Date(`${k}T00:00:00`);
    if (grain === 'month') return a.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    const b = new Date(a); b.setDate(b.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return `${f(a)} – ${f(b)}`;
  } catch { return k; }
};
const pctChange = (cur, prev) => (prev > 0 ? ((cur - prev) / prev) * 100 : null);

function Delta({ value }) {
  if (value == null) return <span className="text-gray-300">—</span>;
  const up = value >= 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${up ? 'text-emerald-700' : 'text-rose-700'}`}>
      <Icon className="w-3 h-3" />
      %{Math.abs(value).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
    </span>
  );
}

function Thumb({ src, alt, onPreview }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <div className="w-14 h-14 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center shrink-0">
        <ImageOff className="w-4 h-4 text-gray-400" />
      </div>
    );
  }
  // Büyük önizleme sayfa seviyesinde `fixed` çiziliyor: hücre içinde absolute
  // olunca sticky kolonun ve kaydırma kutusunun altında kalıyordu.
  return (
    <img
      src={src} alt={alt} loading="lazy" onError={() => setBroken(true)}
      onMouseEnter={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onPreview({ src, x: r.right + 8, y: Math.min(r.top, window.innerHeight - 248) });
      }}
      onMouseLeave={() => onPreview(null)}
      className="w-14 h-14 rounded-lg object-contain bg-white border border-gray-200 shrink-0 cursor-zoom-in"
    />
  );
}

export default function High5ProductSalesPeriods() {
  const { selectedBrand } = useAuth();
  const [grain, setGrain] = useState('week');
  const [periods, setPeriods] = useState(12);
  // Özel aralık: `range` uygulanmış uçlar, `draft` kutulardaki henüz uygulanmamış değer.
  const [range, setRange] = useState(null);
  const [draft, setDraft] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [q, setQ] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  // sortKey: 'total' | 'name' | 'delta' | <dönem anahtarı>
  const [sortKey, setSortKey] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [limit, setLimit] = useState(PAGE);
  const [preview, setPreview] = useState(null);

  const grainDef = GRAINS.find(g => g.key === grain);

  const fetchData = useCallback(async (refresh = false) => {
    if (!selectedBrand?.id) { setData(null); return; }
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams(range
        ? { grain, start: range.start, end: range.end }
        : { grain, periods: String(periods) });
      if (refresh) qs.set('refresh', '1');
      const r = await fetch(`${API_BASE}/api/sku-feed/product-sales-periods?${qs}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id },
      });
      const body = await r.json().catch(() => null);
      if (!r.ok) throw new Error(body?.error || `HTTP ${r.status}`);
      setData(body);
    } catch (e) {
      setError(e.message); setData(null);
    } finally { setLoading(false); }
  }, [selectedBrand?.id, grain, periods, range]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setLimit(PAGE); }, [grain, periods, range, q, brandFilter, sortKey, sortDir]);

  const changeGrain = (g) => {
    if (g === grain) return;
    const def = GRAINS.find(x => x.key === g);
    setGrain(g);
    // Özel aralık korunur, yalnız kırılım değişir.
    if (!range) setPeriods(def.def);
    // Dönem anahtarları değişiyor; dönem kolonuna göre sıralama boşa düşmesin.
    if (sortKey !== 'total' && sortKey !== 'name' && sortKey !== 'delta') setSortKey('total');
  };

  const products = useMemo(() => data?.products || [], [data]);
  // Kolonlar en yeni dönem solda — en çok bakılan yer Toplam'ın hemen yanı.
  const periodKeys = useMemo(() => [...(data?.periods || [])].reverse(), [data]);
  // Yarım dönemler: hazır seçimde son dönem "devam ediyor", özel aralıkta
  // aralığa tam oturmayan ilk/son dönem "kısmi".
  const partialKeys = useMemo(() => {
    const ks = data?.periods || [];
    return new Set([data?.partialFirst ? ks[0] : null, data?.partialLast ? ks[ks.length - 1] : null].filter(Boolean));
  }, [data]);
  const partWord = data?.custom ? 'kısmi' : 'devam ediyor';
  // Değişim: son TAMAMLANMIŞ dönem vs bir önceki. Yarım dönemi kıyaslamak her
  // ürünü düşüşte gösterir.
  const [cmpCur, cmpPrev] = useMemo(() => {
    const full = (data?.periods || []).filter(k => !partialKeys.has(k));
    return [full[full.length - 1] || null, full[full.length - 2] || null];
  }, [data, partialKeys]);

  const brands = useMemo(() => {
    const m = new Map();
    for (const p of products) m.set(p.brand || '(markasız)', (m.get(p.brand || '(markasız)') || 0) + p.revenue);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [products]);

  const rows = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase('tr-TR');
    let list = products.filter(p => {
      if (brandFilter && (p.brand || '(markasız)') !== brandFilter) return false;
      if (!needle) return true;
      return [p.name, p.title, p.id, p.brand, p.variant]
        .some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    });
    const val = (p) => {
      if (sortKey === 'total') return p.revenue;
      if (sortKey === 'name') return p.name || '';
      if (sortKey === 'delta') {
        const d = pctChange(p.cells[cmpCur]?.[0] || 0, p.cells[cmpPrev]?.[0] || 0);
        return d == null ? -Infinity : d;
      }
      return p.cells[sortKey]?.[0] || 0;
    };
    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = val(a); const vb = val(b);
      if (typeof va === 'string') return va.localeCompare(vb, 'tr') * dir;
      return (va - vb) * dir || (b.revenue - a.revenue);
    });
    return list;
  }, [products, q, brandFilter, sortKey, sortDir, cmpCur, cmpPrev]);

  const filteredTotals = useMemo(() => {
    const t = { revenue: 0, quantity: 0, cells: {} };
    for (const p of rows) {
      t.revenue += p.revenue; t.quantity += p.quantity;
      for (const [k, v] of Object.entries(p.cells)) {
        const c = t.cells[k] || (t.cells[k] = [0, 0]);
        c[0] += v[0]; c[1] += v[1];
      }
    }
    return t;
  }, [rows]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };
  const SortMark = ({ k }) => (sortKey === k
    ? (sortDir === 'desc' ? <ArrowDown className="w-3 h-3 inline" /> : <ArrowUp className="w-3 h-3 inline" />)
    : null);

  // Görseller dosyaya gömülü olsun diye Excel/PDF sunucuda üretiliyor (görsel
  // CDN'i CORS vermiyor, tarayıcı görseli okuyamıyor). Sıra + filtre `ids` ile gider.
  const [exporting, setExporting] = useState(null);
  const downloadFile = async (format) => {
    if (!selectedBrand?.id || !rows.length) return;
    setExporting(format);
    try {
      const filterLabel = [brandFilter, q.trim() && `arama: "${q.trim()}"`].filter(Boolean).join(' · ');
      const r = await fetch(`${API_BASE}/api/sku-feed/product-sales-periods/export`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-Brand-ID': selectedBrand.id, 'Content-Type': 'application/json' },
        body: JSON.stringify({ format, grain, periods, ...(range || {}), ids: rows.map(p => p.id), filterLabel }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `high5-urun-satislari-${grain === 'month' ? 'aylik' : 'haftalik'}-${range ? `${range.start}_${range.end}` : data?.lastDate || ''}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      alert(`Dosya hazırlanamadı: ${e.message}`);
    } finally { setExporting(null); }
  };

  const curTotal = data?.totals?.[cmpCur]?.[0] || 0;
  const prevTotal = data?.totals?.[cmpPrev]?.[0] || 0;
  const visible = rows.slice(0, limit);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <Card className="rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50/70 to-white">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl border border-indigo-200 bg-indigo-50 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Ürün Satışları</h1>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ürün bazında ciro ve adet · GA4 satın alma verisi
                  {data?.lastDate ? <> · son veri <strong>{fmtDay(data.lastDate)}</strong></> : null}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1 p-1 bg-white border border-gray-200 rounded-xl">
                {GRAINS.map(g => (
                  <button
                    key={g.key} type="button" onClick={() => changeGrain(g.key)}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                      grain === g.key ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
                {grainDef.ranges.map(n => (
                  <button
                    key={n} type="button" onClick={() => { setRange(null); setDraft(null); setPeriods(n); }}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      !range && !draft && periods === n ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Son {n} {grainDef.unit}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setDraft(d => d || range || { start: data?.periods?.[0] || '', end: data?.lastDate || '' })}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    range || draft ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <CalendarDays className="w-3.5 h-3.5" />
                  {range ? `${fmtDay(range.start)} – ${fmtDay(range.end)}` : 'Tarih aralığı'}
                </button>
              </div>
              {draft && (
                <form
                  className="inline-flex items-center gap-1.5 p-1 bg-white border border-gray-200 rounded-xl"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!draft.start || !draft.end || draft.start > draft.end) return;
                    if (sortKey !== 'total' && sortKey !== 'name' && sortKey !== 'delta') setSortKey('total');
                    setRange({ start: draft.start, end: draft.end });
                    setDraft(null);
                  }}
                >
                  <input
                    type="date" value={draft.start} min={data?.firstDate} max={draft.end || data?.lastDate}
                    onChange={(e) => setDraft(d => ({ ...d, start: e.target.value }))}
                    className="px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  />
                  <span className="text-xs text-gray-400">–</span>
                  <input
                    type="date" value={draft.end} min={draft.start || data?.firstDate} max={data?.lastDate}
                    onChange={(e) => setDraft(d => ({ ...d, end: e.target.value }))}
                    className="px-2 py-1 text-xs border border-gray-200 rounded-lg"
                  />
                  <button
                    type="submit" disabled={!draft.start || !draft.end || draft.start > draft.end}
                    className="px-3 py-1 text-xs font-semibold text-white bg-indigo-600 rounded-lg disabled:opacity-50"
                  >
                    Uygula
                  </button>
                  <button
                    type="button" onClick={() => setDraft(null)}
                    className="px-2 py-1 text-xs text-gray-500 hover:text-gray-900"
                  >
                    Vazgeç
                  </button>
                </form>
              )}
              <button
                type="button" onClick={() => fetchData(true)} disabled={loading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </button>
              {[{ f: 'xlsx', l: 'Excel' }, { f: 'pdf', l: 'PDF' }].map(o => (
                <button
                  key={o.f} type="button" onClick={() => downloadFile(o.f)} disabled={!rows.length || !!exporting}
                  title={o.f === 'pdf' ? 'Görselli PDF · en yeni 13 dönem' : 'Görselli Excel · tüm dönemler'}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50"
                >
                  {exporting === o.f ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {exporting === o.f ? 'Hazırlanıyor…' : o.l}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI şeridi */}
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
          <div className="px-6 py-4">
            <div className="text-xs text-gray-500">Toplam ciro · {range ? `${fmtDay(data?.rangeStart || range.start)} – ${fmtDay(data?.rangeEnd || range.end)}` : `son ${periods} ${grainDef.unit}`}</div>
            <div className="text-xl font-semibold text-gray-900 tabular-nums mt-1">{fmtMoney(filteredTotals.revenue)}</div>
          </div>
          <div className="px-6 py-4">
            <div className="text-xs text-gray-500">Satış adedi</div>
            <div className="text-xl font-semibold text-gray-900 tabular-nums mt-1">{fmtInt(filteredTotals.quantity)}</div>
          </div>
          <div className="px-6 py-4">
            <div className="text-xs text-gray-500">Satan ürün</div>
            <div className="text-xl font-semibold text-gray-900 tabular-nums mt-1">
              {fmtInt(rows.length)}
              {rows.length !== products.length && <span className="text-sm text-gray-400 font-normal"> / {fmtInt(products.length)}</span>}
            </div>
          </div>
          <div className="px-6 py-4">
            <div className="text-xs text-gray-500">
              {cmpCur ? `${periodLabel(cmpCur, grain)} vs önceki ${grain === 'month' ? 'ay' : 'hafta'}` : 'Son dönem'}
            </div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-xl font-semibold text-gray-900 tabular-nums">{fmtMoneyShort(curTotal)}</span>
              <Delta value={pctChange(curTotal, prevTotal)} />
            </div>
          </div>
        </div>

        {/* Filtreler */}
        <div className="px-6 py-3 flex items-center gap-2 flex-wrap border-b border-gray-100">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={q} onChange={e => setQ(e.target.value)} placeholder="Ürün adı, kodu, renk…"
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-xl w-64 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
          </div>
          <select
            value={brandFilter} onChange={e => setBrandFilter(e.target.value)}
            className="py-1.5 px-2 text-sm border border-gray-200 rounded-xl bg-white"
          >
            <option value="">Tüm markalar</option>
            {brands.map(([b, rev]) => <option key={b} value={b}>{b} · {fmtMoneyShort(rev)}</option>)}
          </select>
          {partialKeys.size > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500 ml-auto">
              <Info className="w-3.5 h-3.5" />
              {data?.custom
                ? <>{[...partialKeys].map(k => periodLabel(k, grain)).join(', ')} aralığa tam girmiyor, yalnız {fmtDay(data.rangeStart)} – {fmtDay(data.rangeEnd)} arası sayıldı</>
                : <>{periodLabel([...partialKeys][0], grain)} devam ediyor ({fmtDay(data.lastDate)} dahil)</>}
              ; değişim son tamamlanan dönemle hesaplanır.
            </span>
          )}
        </div>

        {error && <div className="px-6 py-4 text-sm text-rose-700">Veri alınamadı: {error}</div>}
        {!error && data && data.hasData === false && (
          <div className="px-6 py-8 text-sm text-gray-500">{data.note || 'Veri yok.'}</div>
        )}
        {loading && !data && <div className="px-6 py-10 text-sm text-gray-500">Yükleniyor…</div>}

        {data?.hasData && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-separate border-spacing-0">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className="sticky left-0 z-20 bg-gray-50 text-left font-medium px-4 py-2 border-b border-gray-200 min-w-[320px]">
                    <button type="button" onClick={() => onSort('name')} className="hover:text-gray-900">
                      Ürün <SortMark k="name" />
                    </button>
                  </th>
                  <th className="bg-gray-50 text-right font-medium px-3 py-2 border-b border-gray-200 border-r whitespace-nowrap">
                    <button type="button" onClick={() => onSort('total')} className="hover:text-gray-900">
                      Toplam <SortMark k="total" />
                    </button>
                  </th>
                  <th className="bg-gray-50 text-right font-medium px-3 py-2 border-b border-gray-200 border-r whitespace-nowrap">
                    <button type="button" onClick={() => onSort('delta')} className="hover:text-gray-900" title={cmpCur ? `${periodLabel(cmpCur, grain)} vs ${periodLabel(cmpPrev, grain)}` : ''}>
                      Değişim <SortMark k="delta" />
                    </button>
                  </th>
                  {periodKeys.map(k => (
                    <th key={k} className={`bg-gray-50 text-right font-medium px-3 py-2 border-b border-gray-200 whitespace-nowrap ${partialKeys.has(k) ? 'italic text-gray-400' : ''}`}>
                      <button type="button" onClick={() => onSort(k)} className="hover:text-gray-900">
                        {periodLabel(k, grain)} <SortMark k={k} />
                      </button>
                      {partialKeys.has(k) && <div className="text-[10px] not-italic font-normal">{partWord}</div>}
                    </th>
                  ))}
                </tr>
                <tr className="font-semibold text-gray-900">
                  <td className="sticky left-0 z-20 bg-indigo-50/80 backdrop-blur px-4 py-2 border-b border-gray-200">
                    Toplam {brandFilter || q ? '(filtreli)' : ''}
                  </td>
                  <td className="bg-indigo-50/80 text-right px-3 py-2 border-b border-r border-gray-200 tabular-nums whitespace-nowrap">
                    <div>{fmtMoneyShort(filteredTotals.revenue)}</div>
                    <div className="text-[11px] font-normal text-gray-500">{fmtInt(filteredTotals.quantity)} adet</div>
                  </td>
                  <td className="bg-indigo-50/80 text-right px-3 py-2 border-b border-r border-gray-200">
                    <Delta value={pctChange(filteredTotals.cells[cmpCur]?.[0] || 0, filteredTotals.cells[cmpPrev]?.[0] || 0)} />
                  </td>
                  {periodKeys.map(k => (
                    <td key={k} className="bg-indigo-50/80 text-right px-3 py-2 border-b border-gray-200 tabular-nums whitespace-nowrap">
                      <div>{fmtMoneyShort(filteredTotals.cells[k]?.[0] || 0)}</div>
                      <div className="text-[11px] font-normal text-gray-500">{fmtInt(filteredTotals.cells[k]?.[1] || 0)} adet</div>
                    </td>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(p => {
                  const rowMax = Math.max(1, ...Object.values(p.cells).map(v => v[0]));
                  return (
                    <tr key={p.id} className="group/row hover:bg-gray-50">
                      <td className="sticky left-0 z-10 bg-white group-hover/row:bg-gray-50 px-4 py-2 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <Thumb src={p.image} alt={p.name} onPreview={setPreview} />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate max-w-[230px]" title={p.title || p.name}>{p.name}</div>
                            <div className="text-xs text-gray-500 truncate max-w-[230px]">
                              {[p.brand, p.variant].filter(Boolean).join(' · ')}
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-0.5">
                              <span className="font-mono">{p.id}</span>
                              {p.link && (
                                <a href={p.link} target="_blank" rel="noopener noreferrer" className="hover:text-indigo-600" title="Ürün sayfası">
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-right px-3 py-2 border-b border-r border-gray-100 tabular-nums whitespace-nowrap">
                        <div className="font-semibold text-gray-900" title={fmtMoney(p.revenue)}>{fmtMoneyShort(p.revenue)}</div>
                        <div className="text-[11px] text-gray-500">{fmtInt(p.quantity)} adet</div>
                      </td>
                      <td className="text-right px-3 py-2 border-b border-r border-gray-100">
                        <Delta value={pctChange(p.cells[cmpCur]?.[0] || 0, p.cells[cmpPrev]?.[0] || 0)} />
                      </td>
                      {periodKeys.map(k => {
                        const c = p.cells[k];
                        if (!c || (!c[0] && !c[1])) {
                          return <td key={k} className="text-right px-3 py-2 border-b border-gray-100 text-gray-300">—</td>;
                        }
                        // Satır içi yoğunluk: ürünün kendi en iyi dönemine göre —
                        // ürünün seyri okunur, büyük ürünler tabloyu boğmaz.
                        const a = 0.06 + 0.3 * (c[0] / rowMax);
                        return (
                          <td
                            key={k}
                            className={`text-right px-3 py-2 border-b border-gray-100 tabular-nums whitespace-nowrap ${partialKeys.has(k) ? 'opacity-60' : ''}`}
                            style={{ background: `rgba(79, 70, 229, ${a.toFixed(3)})` }}
                            title={`${periodLabel(k, grain)}: ${fmtMoney(c[0])} · ${fmtInt(c[1])} adet`}
                          >
                            <div className="text-gray-900">{fmtMoneyShort(c[0])}</div>
                            <div className="text-[11px] text-gray-600">{fmtInt(c[1])} adet</div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length > limit && (
              <div className="px-6 py-4 flex items-center justify-center gap-3 text-sm">
                <span className="text-gray-500">{fmtInt(limit)} / {fmtInt(rows.length)} ürün</span>
                <button
                  type="button" onClick={() => setLimit(l => l + PAGE)}
                  className="px-3 py-1.5 text-xs font-medium text-indigo-700 border border-indigo-200 rounded-xl hover:bg-indigo-50"
                >
                  {PAGE} ürün daha göster
                </button>
                <button
                  type="button" onClick={() => setLimit(rows.length)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50"
                >
                  Tümünü göster
                </button>
              </div>
            )}
            {!rows.length && <div className="px-6 py-8 text-sm text-gray-500">Filtreye uyan ürün yok.</div>}
          </div>
        )}
      </Card>
      {preview && (
        <img
          src={preview.src} alt="" aria-hidden="true"
          style={{ left: preview.x, top: Math.max(8, preview.y) }}
          className="fixed z-50 w-60 h-60 object-contain bg-white border border-gray-200 rounded-xl shadow-xl pointer-events-none"
        />
      )}
    </div>
  );
}
